import React, { useState } from 'react';
import { Typography, Alert, Button, Space, Descriptions, Card, Tabs, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import MolStarViewer from '../protein/structure/MolStarViewer';

const { Text } = Typography;
const { TabPane } = Tabs;

interface ESMFoldResultProps {
  taskResult?: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

interface ESMFoldStructure {
  name: string;
  sequence: string;
  pdb_content: string;
  atoms: number;
  residues: number;
  success: boolean;
  error?: string;
}

const ESMFoldResult: React.FC<ESMFoldResultProps> = ({ 
  taskResult, 
  data, 
  title = "ESMFold 蛋白质结构预测结果", 
  showTitle = true 
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // 提取ESMFold结构结果
  const getStructureResults = (): ESMFoldStructure[] => {
    const results: ESMFoldStructure[] = [];
    
    // 优先级1: data参数
    if (data) {
      if (Array.isArray(data.results)) {
        // 多序列结果
        for (const item of data.results) {
          const name = item.sequence_id || item.name || 'Unknown';
          const sequence = item.sequence || item.result?.sequence || '';
          const pdb = item.pdb_content || item.pdb_data || item.result?.pdb_content || item.result?.pdb_data || '';
          const atoms = Number(item.atoms ?? item.result?.atoms ?? 0);
          const residues = Number(item.residues ?? item.result?.residues ?? 0);
          const success = typeof item.success === 'boolean' ? item.success : (item.result?.success !== false);
          const error = item.error || item.result?.error;
          if (pdb) {
            results.push({ name, sequence, pdb_content: pdb, atoms, residues, success, error });
          }
        }
      } else if (data.pdb_content || data.pdb_data) {
        // 单个结果
        results.push({
          name: data.name || 'Protein',
          sequence: data.sequence || '',
          pdb_content: data.pdb_content || data.pdb_data,
          atoms: data.atoms || 0,
          residues: data.residues || 0,
          success: data.success !== false,
          error: data.error
        });
      }
    }
    
    // 优先级2: taskResult.result格式（Profile页面格式）
    if (results.length === 0 && taskResult?.result) {
      if (Array.isArray(taskResult.result.results)) {
        for (const entry of taskResult.result.results) {
          const payload = entry.result ?? entry;
          if (!payload) continue;

          // 多序列数组
          if (Array.isArray(payload.results)) {
            for (const item of payload.results) {
              const name = item.sequence_id || item.name || 'Unknown';
              const sequence = item.sequence || item.result?.sequence || '';
              const pdb = item.pdb_content || item.pdb_data || item.result?.pdb_content || item.result?.pdb_data || '';
              const atoms = Number(item.atoms ?? item.result?.atoms ?? 0);
              const residues = Number(item.residues ?? item.result?.residues ?? 0);
              const success = typeof item.success === 'boolean' ? item.success : (item.result?.success !== false);
              const error = item.error || item.result?.error;
              if (pdb) {
                results.push({ name, sequence, pdb_content: pdb, atoms, residues, success, error });
              }
            }
          } else if (payload.pdb_content || payload.pdb_data) {
            // 单个结果对象
            results.push({
              name: payload.name || 'Protein',
              sequence: payload.sequence || '',
              pdb_content: payload.pdb_content || payload.pdb_data,
              atoms: payload.atoms || 0,
              residues: payload.residues || 0,
              success: payload.success !== false,
              error: payload.error
            });
          }
        }
      } else if (taskResult.result.pdb_content || taskResult.result.pdb_data) {
        // 直接结果
        results.push({
          name: taskResult.result.name || 'Protein',
          sequence: taskResult.result.sequence || '',
          pdb_content: taskResult.result.pdb_content || taskResult.result.pdb_data,
          atoms: taskResult.result.atoms || 0,
          residues: taskResult.result.residues || 0,
          success: taskResult.result.success !== false,
          error: taskResult.result.error
        });
      }
    }
    
    return results;
  };

  const results = getStructureResults();

  if (results.length === 0) {
    return (
      <Alert
        type="info"
        message="无ESMFold结构预测结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  const currentResult = results[selectedIndex];

  // 下载PDB文件
  const downloadPDB = (result: ESMFoldStructure) => {
    const blob = new Blob([result.pdb_content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdb`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 下载所有PDB文件
  const downloadAllPDB = () => {
    results.forEach((result, index) => {
      setTimeout(() => {
        downloadPDB(result);
      }, index * 100);
    });
  };

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 16 }}>{title}</Text>
        </div>
      )}

      {/* 多序列选择 - 左侧列表 */}
      {results.length > 1 && (
        <div style={{ 
          display: 'flex', 
          gap: 16,
          minHeight: 400
        }}>
          {/* 左侧序列列表 */}
          <div style={{ 
            width: 280, 
            minWidth: 280,
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            background: '#fafafa'
          }}>
            <div style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid #d9d9d9',
              fontWeight: 600,
              fontSize: 14
            }}>
              序列列表 ({results.length})
            </div>
            <div style={{ 
              flex: 1, 
              overflow: 'auto',
              padding: '8px 0'
            }}>
              {results.map((result, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    background: selectedIndex === index ? '#e6f7ff' : 'transparent',
                    borderLeft: selectedIndex === index ? '3px solid #1890ff' : '3px solid transparent',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                  onMouseEnter={(e) => {
                    if (selectedIndex !== index) {
                      e.currentTarget.style.background = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedIndex !== index) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span style={{ 
                    flex: 1, 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 13
                  }}>
                    {result.name}
                  </span>
                  {result.success ? (
                    <Tag color="success" style={{ margin: 0, fontSize: 11 }}>✓</Tag>
                  ) : (
                    <Tag color="error" style={{ margin: 0, fontSize: 11 }}>✗</Tag>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 右侧内容区域 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="序列名称">{currentResult.name}</Descriptions.Item>
              <Descriptions.Item label="预测状态">
                {currentResult.success ? (
                  <span style={{ color: '#52c41a' }}>成功</span>
                ) : (
                  <span style={{ color: '#ff4d4f' }}>失败</span>
                )}
              </Descriptions.Item>
              {currentResult.residues > 0 && (
                <Descriptions.Item label="残基数">{currentResult.residues}</Descriptions.Item>
              )}
              {currentResult.atoms > 0 && (
                <Descriptions.Item label="原子数">{currentResult.atoms}</Descriptions.Item>
              )}
              {currentResult.sequence && (
                <Descriptions.Item label="序列长度" span={2}>
                  {currentResult.sequence.length} 个氨基酸
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* 错误信息 */}
            {!currentResult.success && currentResult.error && (
              <Alert
                type="error"
                message="预测失败"
                description={currentResult.error}
                style={{ marginBottom: 16 }}
                showIcon
              />
            )}

            {/* 操作按钮 */}
            {currentResult.success && currentResult.pdb_content && (
              <Space style={{ marginBottom: 16 }}>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={() => downloadPDB(currentResult)}
                >
                  下载当前PDB
                </Button>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={downloadAllPDB}
                >
                  下载所有PDB ({results.length})
                </Button>
              </Space>
            )}

            {/* 3D结构查看器 */}
            {currentResult.success && currentResult.pdb_content && (
              <div style={{ height: 500, border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
                <MolStarViewer 
                  pdbData={currentResult.pdb_content} 
                  title={`${currentResult.name} - ESMFold预测结构`}
                  height={500}
                />
              </div>
            )}

            {/* 序列信息 */}
            {currentResult.sequence && (
              <Card size="small" title="序列信息" style={{ marginTop: 16 }}>
                <Text code style={{ fontSize: '12px', wordBreak: 'break-all', display: 'block' }}>
                  {currentResult.sequence}
                </Text>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* 单序列显示 */}
      {results.length === 1 && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="序列名称">{currentResult.name}</Descriptions.Item>
            <Descriptions.Item label="预测状态">
              {currentResult.success ? (
                <span style={{ color: '#52c41a' }}>成功</span>
              ) : (
                <span style={{ color: '#ff4d4f' }}>失败</span>
              )}
            </Descriptions.Item>
            {currentResult.residues > 0 && (
              <Descriptions.Item label="残基数">{currentResult.residues}</Descriptions.Item>
            )}
            {currentResult.atoms > 0 && (
              <Descriptions.Item label="原子数">{currentResult.atoms}</Descriptions.Item>
            )}
            {currentResult.sequence && (
              <Descriptions.Item label="序列长度" span={2}>
                {currentResult.sequence.length} 个氨基酸
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* 错误信息 */}
          {!currentResult.success && currentResult.error && (
            <Alert
              type="error"
              message="预测失败"
              description={currentResult.error}
              style={{ marginBottom: 16 }}
              showIcon
            />
          )}

          {/* 操作按钮 */}
          {currentResult.success && currentResult.pdb_content && (
            <Space style={{ marginBottom: 16 }}>
              <Button 
                icon={<DownloadOutlined />}
                onClick={() => downloadPDB(currentResult)}
              >
                下载PDB
              </Button>
            </Space>
          )}

          {/* 3D结构查看器 */}
          {currentResult.success && currentResult.pdb_content && (
            <div style={{ height: 600, border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
              <MolStarViewer 
                pdbData={currentResult.pdb_content} 
                title={`${currentResult.name} - ESMFold预测结构`}
                height={600}
              />
            </div>
          )}

          {/* 序列信息 */}
          {currentResult.sequence && (
            <Card size="small" title="序列信息" style={{ marginTop: 16 }}>
              <Text code style={{ fontSize: '12px', wordBreak: 'break-all', display: 'block' }}>
                {currentResult.sequence}
              </Text>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ESMFoldResult;

